import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Chip, Tooltip, alpha, CircularProgress } from '@mui/material';
import DiamondIcon from '@mui/icons-material/Diamond';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { getCrmPalette } from '../theme/brand';
import { useThemeMode } from '../theme/ThemeModeContext';
import { useGetMyCreditsQuery } from '../store/api/creditsApi';
import { useAppSelector } from '../hooks/useAppStore';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const P = getCrmPalette();
const golden = P.brand.golden;

/**
 * Wallet de créditos persistente del user.
 * Se muestra en el topbar global: clickeable → lleva a /mis-creditos.
 * Auto-refresca via tag 'Credits' (evento SSE de Gustavo, recargas admin, etc.)
 */
export function CreditsWalletChip() {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  // En claro, dorado más oscuro para el texto/ícono (mejor contraste sobre fondo claro).
  const goldText = mode === 'light' ? P.brand.goldenDark : golden;
  const user = useAppSelector((s) => s.auth.user);
  const creditsEnabled = useFeatureFlag('credits');
  const { data, isLoading } = useGetMyCreditsQuery(undefined, {
    skip: !user || !creditsEnabled, // No consultar en pantallas sin sesión ni con flag off
  });

  if (!user) return null;
  if (!creditsEnabled) return null;
  if (data && !data.systemEnabled) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={12} sx={{ color: golden }} />
      </Box>
    );
  }
  if (!data) return null;

  const handleClick = () => navigate('/profile?tab=credits');

  if (data.unlimited) {
    return (
      <Tooltip title="Uso ilimitado (platform_admin) — click para ver el panel">
        <ButtonBase onClick={handleClick} sx={{ borderRadius: 999 }}>
          <Chip
            size="small"
            clickable
            icon={<AllInclusiveIcon sx={{ fontSize: 14 }} />}
            label="Ilimitado"
            sx={{
              height: 26,
              bgcolor: alpha('#10B981', 0.12),
              color: '#10B981',
              fontWeight: 700,
              fontSize: 11.5,
              border: `1px solid ${alpha('#10B981', 0.3)}`,
              '& .MuiChip-icon': { color: '#10B981' },
              cursor: 'pointer',
              '&:hover': { bgcolor: alpha('#10B981', 0.2) },
            }}
          />
        </ButtonBase>
      </Tooltip>
    );
  }

  /**
   * El disponible ES el saldo del wallet: es lo único que el cobro respeta.
   *
   * Antes se derivaba del cupo (`monthlyAllowance - usedThisMonth`) para "coincidir con
   * el perfil", pero el perfil nunca usó esa cuenta —MyCreditsView saca el disponible de
   * `data.balance` desde siempre—, así que el cambio logró justo lo contrario: que el
   * chip y la vista mostraran cifras distintas, y ninguna de las dos el saldo real.
   *
   * Lo que se rompía: todo movimiento hecho por un admin quedaba INVISIBLE, porque ni el
   * grant ni el débito tocan `usedThisMonth`. El 15/08 se redistribuyeron créditos entre
   * cinco agentes y el chip siguió mostrando lo mismo que antes: Gabriela y Evelyn veían
   * 0 teniendo 94,96 y 290,58 —el `Math.max(0, …)` aplastaba el resultado porque ya
   * habían consumido más de 300 en el mes—, y Silvia, Yuliana y Florencia veían 300
   * teniendo 0, 150 y 0. Al que le cargan créditos cree que no llegaron; al que se los
   * sacan cree que los tiene y se corta sin entender por qué. (16/08/2026)
   */
  const remaining = data.balance;
  const fmt = (n: number) => n.toLocaleString('es-UY', { maximumFractionDigits: 2 });

  // El cupo sigue sirviendo de ESCALA para decidir qué es "poco" (15% de 300 = 45).
  const lowThreshold = data.monthlyAllowance != null ? data.monthlyAllowance * 0.15 : 50;
  const lowBalance = remaining <= 0 ? 'zero' : remaining <= lowThreshold ? 'warn' : null;
  const color = lowBalance === 'zero' ? '#F87171' : lowBalance === 'warn' ? '#FBBF24' : goldText;

  // Siempre el icono de crédito (Diamond), igual que Imperium — sin el icono de oficina
  // aunque sea pool compartido. El detalle del pool queda en el tooltip.
  const icon = <DiamondIcon sx={{ fontSize: 13 }} />;
  const label = fmt(remaining);

  const tooltip = data.shared
    ? `${fmt(remaining)} créditos disponibles · pool de ${data.officeName ?? 'la oficina'} · Click para ver detalle`
    : `${fmt(remaining)} créditos disponibles · Click para ver detalle`;

  return (
    <Tooltip title={tooltip}>
      <ButtonBase onClick={handleClick} sx={{ borderRadius: 999 }}>
        <Chip
          size="small"
          clickable
          icon={icon}
          label={label}
          sx={{
            height: 26,
            bgcolor: alpha(color, 0.1),
            color,
            fontWeight: 700,
            fontSize: 11.5,
            border: `1px solid ${alpha(color, 0.3)}`,
            '& .MuiChip-icon': { color },
            maxWidth: 220,
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(color, 0.18) },
          }}
        />
      </ButtonBase>
    </Tooltip>
  );
}
